import { useQuery } from 'react-query'
import { api } from '@/lib/api'
import type {
	NetworkStats,
	NetworkThroughput,
	NetworkAverageGasFee,
	NetworkDailyActiveAccounts,
	NetworkDailyTransactionBlocks,
	NetworkDailyTransactions,

	CheckpointPage,
	CheckpointDetails,
	CheckpointTransactionBlocks,

	TransactionBlockRecents,
	TransactionBlockDetails,

	PackageDetails,
	PackageTransactionBlocks,

	AddressDetails,
	AddressBalances,
	AddressTransactionBlocks,

	CoinDetails,

	ObjectDetails,
	SearchResponse
} from '@/lib/api'


// Network
export function useNetworkStats() {
	return useQuery<NetworkStats>({
		queryFn: () => api.getNetworkStats(),
		queryKey: ["networkStats"],
		staleTime: 5000,
		refetchInterval: 5000,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true,
		refetchIntervalInBackground: true
	})
}
export function useNetworkThroughput() {
	return useQuery<NetworkThroughput>({
		queryFn: () => api.getNetworkThroughput(),
		queryKey: ['networkThroughput'],
		staleTime: 5000,
		refetchInterval: 5000,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true,
		refetchIntervalInBackground: true
	})
}
export function useNetworkAverageGasFee() {
	return useQuery<NetworkAverageGasFee>({
		queryFn: () => api.getNetworkAverageGasFee(),
		queryKey: ['networkAverageGasFee'],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}
export function useNetworkDailyActiveAccounts() {
	return useQuery<NetworkDailyActiveAccounts>({
		queryFn: () => api.getNetworkDailyActiveAccounts(),
		queryKey: ['networkDailyActiveAccounts'],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}
export function useNetworkDailyTransactionBlocks() {
	return useQuery<NetworkDailyTransactionBlocks>({
		queryFn: () => api.getNetworkDailyTransactionBlocks(),
		queryKey: ['networkDailyTransactionBlocks'],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}
export function useNetworkDailyTransactions() {
	return useQuery<NetworkDailyTransactions>({
		queryFn: () => api.getNetworkDailyTransactions(),
		queryKey: ['networkDailyTransactions'],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}

// Checkpoints
export function useCheckpointRecents(limit?: number, cursor?: string | null) {
	return useQuery<CheckpointPage>({
		queryFn: () => api.getCheckpointRecents(limit, cursor),
		queryKey: ['checkpointRecents', limit, cursor],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}
export function useCheckpoint(id: string) {
	return useQuery<CheckpointDetails>({
		queryFn: () => api.getCheckpoint(id),
		queryKey: ['checkpoint', id],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}
export function useCheckpointTransactionBlocks(id: string, limit?: number, offset?: number) {
	return useQuery<CheckpointTransactionBlocks>({
		queryFn: () => api.getCheckpointTransactionBlocks(id, limit, offset),
		queryKey: ['checkpointTransactionBlocks', id, limit, offset],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}

// Transaction Blocks
export function useTransactionBlockRecents(limit?: number, sequenceNumber?: string | null, txOffset?: number | null) {
	return useQuery<TransactionBlockRecents>({
		queryFn: () => api.getTransactionBlockRecents(limit, sequenceNumber, txOffset),
		queryKey: ['transactionBlockRecents', limit, sequenceNumber, txOffset],
		staleTime: Infinity,
		refetchOnWindowFocus: false,
		suspense: false,
		keepPreviousData: true
	})
}
export function useTransactionBlockDetails(digest: string) {
	return useQuery<TransactionBlockDetails>({
		queryFn: () => api.getTransactionBlockDetails(digest),
		queryKey: ['transactionBlockDetails', digest],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}

// Packages
export function usePackageDetails(id: string) {
	return useQuery<PackageDetails>({
		queryFn: () => api.getPackageDetails(id),
		queryKey: ['packageDetails', id],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}
export function usePackageTransactionBlocks(id: string, limit?: number, cursor?: string | null) {
	return useQuery<PackageTransactionBlocks>({
		queryFn: () => api.getPackageTransactionBlocks(id, limit, cursor),
		queryKey: ['packageTransactionBlocks', id, limit, cursor],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}

// Accounts

export function useAccountsDetails(id: string) {
	return useQuery<AddressDetails>({
		queryFn: () => api.getAddressDetails(id),
		queryKey: ['accountDetails', id],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}
export function useAccountBalances(id: string) {
	return useQuery<AddressBalances>({
		queryFn: () => api.getAddressBalances(id),
		queryKey: ['addressBalances', id],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}
export function useAccountFromTransactionBlocks(id: string, limit?: number, cursor?: string | null) {
	return useQuery<AddressTransactionBlocks>({
		queryFn: () => api.getAddressFromTransactions(id, limit, cursor),
		queryKey: ['accountFromTransactions', id, limit, cursor],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}
export function useAccountToTransactionBlocks(id: string, limit?: number, cursor?: string | null) {
	return useQuery<AddressTransactionBlocks>({
		queryFn: () => api.getAddressToTransactions(id, limit, cursor),
		queryKey: ['accountToTransactions', id, limit, cursor],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}

// Coins
export function useCoinDetails(id: string) {
	return useQuery<CoinDetails>({
		queryFn: () => api.getCoinDetails(id),
		queryKey: ['coinDetails', id],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}


// Objects
export function useObjectDetails(id: string) {
	return useQuery<ObjectDetails>({
		queryFn: () => api.getObjectDetails(id),
		queryKey: ['objectDetails', id],
		staleTime: Infinity,
		refetchOnWindowFocus: false
	})
}
